// React
import React, { useEffect, useRef, useId } from "react";

// External libraries
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/sharp-regular-svg-icons";
import { faSpinner, faCircleInfo as fasCircleInfo, faCircleQuestion } from "@fortawesome/sharp-solid-svg-icons";
import ReactDOM from "react-dom";

// Internal
import { Button } from "../buttons/button";
import { CloseButton } from "../buttons/close-button.jsx";
import { getDataTestId } from "../functions/data-test-ids";
import { useOptionalHelpContext } from "../help-context";
import { ModalButtonAlignment, ModalButtonType, ModalWidth } from "./modal-constants";
import { ModalSidebar } from "./modal-sidebar";

export { ModalBanner } from "./modal-banner";
export { ModalSidebar } from "./modal-sidebar";

// CSS
import "./modal.css";

const Markdown = React.lazy(() => import("../markdown/markdown"));

export function ModalExplanation(props) {
    if (!props.Explanation || !props.Explanation.Message) {
        return null;
    }

    return (
        <div className="eone-ui-modal-explanation">
            <Markdown>
                {props.Explanation.Message}
            </Markdown>
        </div>
    );
}

export function ModalMessage(props) {
    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Spinner) {
        return (
            <div className="eone-ui-modal-message-container">
                <FontAwesomeIcon icon={faSpinner} spin={true} />
                <label className="eone-ui-modal-message" data-testid={props.DataTestId}>{props.Message}</label>
            </div>
        );
    }

    return (
        <div className="eone-ui-modal-message-container">
            <label data-testid={props.DataTestId}>{props.Message}</label>
        </div>
    );
}

export function ModalSection(props) {
    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    return (
        <div className="eone-ui-modal-section-container">
            <label className="eone-ui-modal-section-title">{props.Title}</label>
            <div className="eone-ui-modal-section-body">
                {props.children}
            </div>
        </div>
    );
}

export function ModalForm(props) {
    const [body, enableAnimations] = useAutoAnimate();

    const getContainerClass = () => {
        let className = "eone-ui-modal-form ";
        if (props.ContainerClass) {
            className += props.ContainerClass;
        }
        return className;
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Animate) {
        return (
            <div className={getContainerClass()} ref={body}>
                {props.children}
            </div>
        );
    }

    return (
        <div className={getContainerClass()}>
            {props.children}
        </div>
    );
}

export function ModalFooterButton(props) {
    const getClass = () => {
        switch (props.Type) {
            case ModalButtonType.Primary:
                return "eone-ui-modal-footer-button-primary";
            case ModalButtonType.Secondary:
                return "eone-ui-modal-footer-button-secondary";
            case ModalButtonType.Danger:
                return "eone-ui-modal-footer-button-danger";
            case ModalButtonType.Link:
                return "eone-ui-modal-footer-button-link";
            case ModalButtonType.Processing:
                return "eone-ui-modal-footer-button-processing";
            default:
                return "";
        }
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const className = "eone-ui-modal-footer-button " + getClass();
    if (props.Icon) {
        return (
            <button type="button" className={className} title={props.Title || props.Label} onClick={props.OnClick} disabled={props.Disabled} data-testid={props.DataTestId}>
                <FontAwesomeIcon icon={props.Icon} spin={props.IconSpin} fixedWidth />
                <div className="eone-ui-modal-footer-button-label">{props.Label}</div>
            </button>
        );
    }

    return (
        <button type="button" className={className} title={props.Title || props.Label} onClick={props.OnClick} disabled={props.Disabled} data-testid={props.DataTestId}>
            {props.Label}
        </button>
    );
}

function ModalHelpLinkButton(props) {
    const helpContext = useOptionalHelpContext();

    const navigateToHelp = () => {
        helpContext.push({ Id: props.HelpLink, Type: "Article" });
    };

    // No provider means the host has nowhere to show an article, so offer no link.
    if (!helpContext || !props.HelpLink) {
        return null;
    }

    return (
        <Button
            Class={"eone-ui-modal-help-link-button"}
            Icon={faCircleQuestion}
            OnClick={navigateToHelp}
            Title="Show help" />
    );
}

function ModalRequiredFieldsButton(props) {
    if (!props.RequiredFields) {
        return null;
    }

    if (!props.RequiredFields.Visible) {
        return null;
    }

    return (
        <Button
            Icon={props.RequiredFields.Show ? fasCircleInfo : faCircleInfo}
            OnClick={props.RequiredFields.Toggle} />
    );
}

function ModalFooter(props) {
    const getRightButtons = () => {
        if (!props.Buttons) {
            return null;
        }

        return props.Buttons.filter(button => button.Alignment !== ModalButtonAlignment.Left).map((button, index) => {
            return (
                <ModalFooterButton
                    key={button.DataTestId ?? button.Label ?? index}
                    Label={button.Label}
                    Title={button.Title}
                    Icon={button.Icon}
                    IconSpin={button.IconSpin}
                    Type={button.Type}
                    Visible={button.Visible}
                    Disabled={button.Disabled}
                    OnClick={button.OnClick}
                    DataTestId={getDataTestId(props.DataTestId, button.DataTestId || button.Label)} />
            );
        });
    };

    const getLeftButtons = () => {
        if (!props.Buttons) {
            return null;
        }

        return props.Buttons.filter(button => button.Alignment === ModalButtonAlignment.Left).map((button, index) => {
            return (
                <ModalFooterButton
                    key={button.DataTestId ?? button.Label ?? index}
                    Label={button.Label}
                    Title={button.Title}
                    Icon={button.Icon}
                    IconSpin={button.IconSpin}
                    Type={button.Type}
                    Visible={button.Visible}
                    Disabled={button.Disabled}
                    OnClick={button.OnClick}
                    DataTestId={getDataTestId(props.DataTestId, button.DataTestId || button.Label)} />
            );
        });
    };

    if (!props.Visible) {
        return null;
    }
    if (props.CustomFooter) {
        return props.CustomFooter;
    }
    return (
        <div className="eone-ui-modal-footer">
            <div className="eone-ui-modal-footer-button-container">
                <ModalHelpLinkButton HelpLink={props.HelpLink} />
                <ModalRequiredFieldsButton RequiredFields={props.RequiredFields} />
                {getLeftButtons()}
            </div>
            <div className="eone-ui-modal-footer-button-container">
                {getRightButtons()}
            </div>
        </div>
    );
}

function ModalHeaderButton(props) {
    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Icon) {
        return (
            <button type="button" className="eone-ui-modal-header-button" title={props.Title || props.Label} onClick={props.OnClick} data-testid={props.DataTestId}>
                <FontAwesomeIcon icon={props.Icon} fixedWidth />
                <div className="eone-ui-modal-header-button-label">{props.Label}</div>
            </button>
        );
    }

    return (
        <button type="button" className="eone-ui-modal-header-button" title={props.Title || props.Label} onClick={props.OnClick} data-testid={props.DataTestId}>
            {props.Label}
        </button>
    );
}

function ModalHeader(props) {
    const getSubtitle = () => {
        if (props.Subtitle) {
            return (
                <div className={"eone-ui-modal-header-subtitle"}>{props.Subtitle}</div>
            );
        }
    };

    if (!props.Visible) return null;

    return (
        <div className="eone-ui-modal-header">
            <div>
                <div className="eone-ui-modal-header-title" id={props.TitleId}>{props.Title}</div>
                {getSubtitle()}
            </div>
            <div className="eone-ui-modal-header-actions">
                {props.HeaderActions}
                <CloseButton
                    Visible={!props.HideClose}
                    Close={props.Close}
                    DataTestId={getDataTestId(props.DataTestId, "close-button")} />
            </div>
        </div>
    );
}

const ModalPortal = ({ children }) => {
    const container = document.getElementById("modal-root");
    if (container)
        return ReactDOM.createPortal(children, container);
    return children;
};

/**
 * Which way the pages just moved, for the AnimatePages slide.
 *
 * Derived from the ORDER of the Pages array rather than from the caller, so a modal opts in with a single
 * prop and nothing has to tell the component which of its steps is "forwards". A page that is no longer in
 * the array, or a first paint with nothing to compare against, is "none" - see the reset in Modal.
 */
function getPageDirection(pages, from, to) {
    if (from === undefined || from === to || !pages) return "none";
    const fromIndex = pages.findIndex(page => page.Value === from);
    const toIndex = pages.findIndex(page => page.Value === to);
    if (fromIndex < 0 || toIndex < 0) return "none";
    return toIndex > fromIndex ? "forward" : "back";
}

export function Modal(props) {
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);
    const closeRef = useRef(null);
    const hideCloseRef = useRef(false);
    const titleId = useId();
    /* The page the body is showing and how it got there, held together in ONE ref so the pair cannot
       disagree. Updated during render rather than in an effect, because the direction is needed on the very
       render that changes the page - an effect would run a frame too late and the entrance would already
       have played in the wrong direction.
       Guarded on the value actually changing, which is what makes it safe to do during render: StrictMode's
       double invoke finds the ref already up to date on its second pass and leaves the direction alone,
       instead of recomputing it as "none" and killing the animation in development. */
    const pageRef = useRef({ Value: undefined, Direction: "none" });

    const closeModal = () => {
        props.Close();
    };

    closeRef.current = props.Close;
    hideCloseRef.current = props.HideClose;

    // Focus trap and keyboard handling
    useEffect(() => {
        if (!props.Show) return;

        previousFocusRef.current = document.activeElement;

        const modalElement = modalRef.current;
        if (modalElement) {
            const focusableElements = modalElement.querySelectorAll(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
            );
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === "Escape" && !hideCloseRef.current) {
                closeRef.current?.();
                return;
            }

            if (e.key === "Tab" && modalElement) {
                const focusableElements = modalElement.querySelectorAll(
                    "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])"
                );
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
                previousFocusRef.current.focus();
            }
        };
    }, [props.Show]);

    const getClass = () => {
        switch (props.Width) {
            case ModalWidth.Narrow:
                return "eone-ui-modal eone-ui-modal-narrow";
            case ModalWidth.ExtraNarrow:
                return "eone-ui-modal eone-ui-modal-extra-narrow";
            case ModalWidth.Wide:
                return "eone-ui-modal eone-ui-modal-wide";
            case ModalWidth.ExtraWide:
                return "eone-ui-modal eone-ui-modal-extra-wide";
            default:
                return "eone-ui-modal eone-ui-modal-normal";
        }
    };

    const getActivePageContent = () => {
        if (!props.Pages) {
            return props.children;
        }
        const activePage = props.Pages.find(page => page.Value === props.ActivePageValue);
        if (activePage && activePage.Content !== undefined) {
            return activePage.Content;
        }
        return props.children;
    };

    /* Closed modals forget where they were. This component stays mounted while hidden (the null return
       below is after the hooks), and callers routinely reset to their first page on close - so without
       this, reopening would compare the first page against the last one and slide in BACKWARDS on top of
       the modal's own open animation. */
    if (!props.Show) {
        pageRef.current = { Value: undefined, Direction: "none" };
    } else if (pageRef.current.Value !== props.ActivePageValue) {
        pageRef.current = {
            Value: props.ActivePageValue,
            Direction: getPageDirection(props.Pages, pageRef.current.Value, props.ActivePageValue)
        };
    }

    /* Opt-in, like ModalForm's Animate: every paged modal in every consuming app would otherwise start
       sliding without being asked. Without it this returns the content exactly as it always has.

       Keyed on the page value, and that key is the whole mechanism: both pages' content sits at the same
       position in the tree, so React would reconcile one wrapper into the other and the keyframe would
       never replay. */
    const renderPageContent = () => {
        if (!props.AnimatePages) {
            return getActivePageContent();
        }

        return (
            <div
                className={`eone-ui-modal-page eone-ui-modal-page-${pageRef.current.Direction}`}
                key={props.ActivePageValue}>
                {getActivePageContent()}
            </div>
        );
    };

    const getBody = () => {
        if (props.Pages) {
            const contentStyle = props.PagesMinHeight !== undefined
                ? { minHeight: typeof props.PagesMinHeight === "number" ? `${props.PagesMinHeight}px` : props.PagesMinHeight }
                : undefined;
            const visiblePages = props.Pages.filter(page => page.Visible !== false);
            if (visiblePages.length <= 1) {
                /* A page can ask for the body's own padding to be dropped so its content runs to the
                   edges - progress views and other full-bleed content read as the body itself. */
                const activePage = props.Pages.find(page => page.Value === props.ActivePageValue);
                const bodyClass = activePage && activePage.FullBleed
                    ? "eone-ui-modal-body eone-ui-modal-body-full-bleed"
                    : "eone-ui-modal-body";
                return (
                    <div className={bodyClass} style={contentStyle}>
                        {renderPageContent()}
                    </div>
                );
            }
            return (
                <div className="eone-ui-modal-body eone-ui-modal-body-with-sidebar">
                    <ModalSidebar
                        Items={props.Pages}
                        Value={props.ActivePageValue}
                        OnChange={props.OnPageChange}
                        DataTestId={props.SidebarDataTestId || getDataTestId(props.DataTestId, "sidebar")} />
                    <div className="eone-ui-modal-sidebar-content" style={contentStyle}>
                        {renderPageContent()}
                    </div>
                </div>
            );
        }

        return (
            <div className="eone-ui-modal-body">
                {props.children}
            </div>
        );
    };

    if (!props.Show) {
        return null;
    }
    return (
        <ModalPortal>
            <div className={props.Show ? "eone-ui-modal-container fade-in" : "eone-ui-modal-container fade-out"}>
                <div className={getClass()} ref={modalRef} role="dialog" aria-modal="true" aria-labelledby={titleId}>
                    <ModalHeader
                        Visible={!props.HideHeader}
                        Title={props.Title}
                        TitleId={titleId}
                        Subtitle={props.Subtitle}
                        Close={closeModal}
                        HideClose={props.HideClose}
                        HeaderActions={props.HeaderActions}
                        DataTestId={props.DataTestId} />
                    {getBody()}
                    <ModalFooter
                        CustomFooter={props.CustomFooter}
                        Visible={!props.HideFooter}
                        Buttons={props.FooterButtons}
                        RequiredFields={props.RequiredFields}
                        DataTestId={props.DataTestId}
                        HelpLink={props.HelpLink} />
                </div>
            </div>
        </ModalPortal>
    );
}